import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormikProps } from 'formik';
import { isFunction, isNull, map } from 'lodash';
import React, { useCallback } from 'react';

import {
  IModal,
  IModalFooterProps,
  IModalImagePreview,
  IModalProperties,
} from './modal-renderer';
import { Button } from '../button/button';
import { FormValues } from '../form/form';
import {
  Alert,
  alpha,
  Box,
  DialogActions,
  DialogContent,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';

/** ModalInner only handles standard modal types — imagePreview is rendered directly in ModalRenderer. */
export type IModalStandard<TForm extends FormValues<TForm>> = Exclude<
  IModal<TForm>,
  IModalImagePreview
>;

interface IModalInnerPropsBase<TForm extends FormValues<TForm>> {
  modal: IModalStandard<TForm>;
  modalProps: IModalProperties;
  formikProps?: FormikProps<TForm>;
  /**
   * Whether the dialog is covering the whole screen. Only used for padding:
   * a fullscreen modal owns the viewport, so it has to clear the iOS home
   * indicator itself, and its 24px side gutters are a third of a phone's width.
   */
  fullScreen?: boolean;
}

interface IModalInnerPropsSave<TForm extends FormValues<TForm>>
  extends IModalInnerPropsBase<TForm> {
  formikProps: FormikProps<TForm>;
}

export type IModalInnerProps<TForm extends FormValues<TForm>> =
  | IModalInnerPropsBase<TForm>
  | IModalInnerPropsSave<TForm>;

export default function ModalInner<TForm extends FormValues<TForm>>({
  modal,
  modalProps,
  formikProps,
  fullScreen = false,
}: IModalInnerProps<TForm>) {
  const renderModalBody = useCallback(() => {
    if (isFunction(modal.body)) {
      if (
        (formikProps && modal.type === 'save') ||
        (formikProps && modal.type === 'readOnly')
      ) {
        return modal.body(formikProps);
      }
      if (modal.type === 'info') {
        return modal.body(modalProps);
      }

      throw new Error(`Modal type doesn't support a function body.`);
    }
    return modal.body;
  }, [formikProps, modal, modalProps]);

  const renderModalFooter = useCallback(() => {
    if (isFunction(modal.footer)) {
      // Cast footer to a known signature to resolve TypeScript's intersection
      // type inference across the modal union — safe because ModalInner is only
      // ever called with standard (non-imagePreview) modal types.
      const footerFn = modal.footer as (
        props: IModalFooterProps<TForm>,
      ) => React.ReactNode;
      return footerFn({ formikProps, modalProps });
    }
    return modal.footer;
  }, [formikProps, modal, modalProps]);

  // The header's icon: the modal's own (MUI or Font Awesome) if it gave one.
  const icon =
    modal.muiIcon ??
    (modal.icon ? <FontAwesomeIcon icon={modal.icon} /> : null);

  return (
    <>
      {!modalProps.hideHeader && (
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{
            px: { xs: 2, sm: 3 },
            pt: { xs: 2, sm: 2.5 },
            pb: 2,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          {icon && (
            <Box
              sx={{
                width: 40,
                height: 40,
                flexShrink: 0,
                borderRadius: 2,
                display: 'grid',
                placeItems: 'center',
                color: 'primary.main',
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                '& svg': { fontSize: 20 },
              }}
            >
              {icon}
            </Box>
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              // The id the dialog is labelled by, so it has an accessible name.
              component="h2"
              id={modalProps.titleId}
              sx={{ fontWeight: 700, lineHeight: 1.3, fontSize: '1.125rem' }}
            >
              {modal.title}
            </Typography>
            {modal.subtitle && (
              <Typography variant="body2" color="text.secondary">
                {modal.subtitle}
              </Typography>
            )}
          </Box>
          <IconButton
            // "Close dialog", as the app's hand-rolled dialogs label theirs —
            // and not plain "Close", which several modals already use for a
            // footer button.
            aria-label="Close dialog"
            size="small"
            onClick={modalProps.onClose}
            disabled={modalProps.closeBlocked}
            sx={{ alignSelf: 'flex-start', color: 'text.secondary' }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Stack>
      )}

      <DialogContent
        autoFocus
        sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}
      >
        {renderModalBody()}
      </DialogContent>

      {modalProps.error && (
        <Alert severity="error" sx={{ mx: { xs: 2, sm: 3 }, mb: 1.5 }}>
          {modalProps.error}
        </Alert>
      )}

      {/* `footer: null` means the body carries its own buttons — no bar. */}
      {!isNull(modal.footer) && (
        <DialogActions
          sx={{
            px: { xs: 2, sm: 3 },
            py: 1.5,
            gap: 1,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: (theme) =>
              alpha(
                theme.palette.text.primary,
                theme.palette.mode === 'dark' ? 0.04 : 0.02,
              ),
            '& > :not(style) ~ :not(style)': { ml: 0 },
            // Only fullscreen needs this: it owns the viewport, so its action
            // row would otherwise sit under the iOS home indicator (and behind
            // Safari's toolbar). Matches multi-step-modal-renderer.
            ...(fullScreen && {
              pb: 'calc(env(safe-area-inset-bottom) + 12px)',
            }),
          }}
        >
          {modal.footer
            ? renderModalFooter()
            : map(modalProps.buttons, (button, idx) => (
                <Button key={idx} {...button} />
              ))}
        </DialogActions>
      )}
    </>
  );
}
